import { useEffect } from 'react';
import styled from 'styled-components';
import { FlexColumn, Dialog } from '@elementary/common-ui/components';
import FormControls from '@elementary/common-ui/components/FormControls';
import { Tooltip } from '@mui/material';
import { TextField } from '@elementary/common-ui/components';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { TeamUser, UserUpdateForm } from 'shared/types';
import { ReactComponent as EditIcon } from '@elementary/common-ui/assets/edit.svg';
import { Role } from '../shared/types';
import RoleSelectFormField from './RoleSelectFormField';

const Wrapper = styled(FlexColumn)`
  gap: 24px;
`;

const HelperText = styled.span`
  color: ${({ theme }) => theme.palette.common.gray2};
  font-size: 12px;
  line-height: 16px;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirmButtonClick: (form: UserUpdateForm) => void;
  cancelText?: string;
  confirmText: string;
  errorMessage?: string;
  updateUserLoading?: boolean;
  user?: TeamUser;
  roles?: Role[];
};

const UpdateUserModal = ({
  open,
  onClose,
  onConfirmButtonClick,
  cancelText = 'Cancel',
  confirmText,
  errorMessage,
  updateUserLoading,
  user,
  roles,
}: Props) => {
  const methods = useForm<UserUpdateForm>({ mode: 'onBlur' });
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
  } = methods;

  const isExternallyManaged = user?.managedBy !== 'frontegg';

  const onSubmit: SubmitHandler<UserUpdateForm> = (form: UserUpdateForm) => {
    const updatedForm = {
      ...form,
      name: isExternallyManaged ? null : form.name,
    };
    onConfirmButtonClick(updatedForm);
  };

  const defaultValues = {
    name: user?.name,
    role_id: user?.roles[0]?.id || null,
    userId: user?.id,
  };

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit user"
      titleIcon={<EditIcon height={16} width={16} />}
      confirmText={confirmText}
      cancelText={cancelText}
      onConfirmButtonClick={() => handleSubmit(onSubmit)()}
      confirmDisabled={!isDirty || !isValid}
      isLoading={updateUserLoading}
      message={
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Wrapper>
              <FormControls.InputWrapper>
                <Tooltip
                  title={
                    isExternallyManaged
                      ? 'Unable to update name for externally managed users'
                      : undefined
                  }
                  placement="top"
                >
                  <TextField
                    {...register('name', {
                      required: 'This field is required',
                    })}
                    variant="outlined"
                    size="large"
                    label="Full name"
                    disabled={isExternallyManaged}
                  />
                </Tooltip>
                {!!errors.name?.message && (
                  <FormControls.ErrorMessage variant="caption">
                    {errors.name?.message}
                  </FormControls.ErrorMessage>
                )}
              </FormControls.InputWrapper>

              <RoleSelectFormField roles={roles} />
              {!!user?.inherited_roles?.length && (
                <HelperText>
                  Inherited roles are managed from Groups and cannot be edited
                  here.
                </HelperText>
              )}
              {!!errors.role_id?.message && (
                <FormControls.ErrorMessage variant="caption">
                  {errors.role_id?.message}
                </FormControls.ErrorMessage>
              )}

              <FormControls.InputWrapper>
                {!!errorMessage && (
                  <FormControls.ErrorMessage variant="caption">
                    {errorMessage}
                  </FormControls.ErrorMessage>
                )}
              </FormControls.InputWrapper>
            </Wrapper>
          </form>
        </FormProvider>
      }
    />
  );
};

export default UpdateUserModal;
